import AppEditor from '@/components/AppEditor';
import AppForm from '@/components/AppForm';
import { SuccessResponseModal } from '@/components/AppModal/modals';
import AppPageTitle from '@/components/AppPageTitle';
import FormUpload from '@/components/common/FormUpload';
import { useUnsaved } from '@/contexts/UnsavedContext';
import useFormSubmit from '@/hooks/useFormSubmit';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import {
  getConfig,
  updateConfigDailyQuestByHeart,
  updateConfigEventPromotion,
  updateConfigGeneral,
  updateConfigInfo,
  updateConfigMessageBar,
  updateConfigSms,
} from '@/services/config.service';
import { safeJSONParse } from '@/utils';
import { formatNumber } from '@/utils/formatters';
import { SyncOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Space,
  Form,
  InputNumber,
  Tabs,
  Card,
  Button,
  Tooltip,
  Input,
  Radio,
  Switch,
  Collapse,
} from 'antd';
import React, { useEffect, useMemo, useState, useRef } from 'react';

const TAB_KEY = {
  GENERAL: 'general',
  TERM_CONDITIONS: 'term_conditions',
  PRIZE_INFO: 'prize_info',
  SMS: {
    ROOT: 'sms',
    TOPUP_PRIZE: 'topup_prize',
    PHYSICAL_PRIZE: 'physical_prize',
    AFTER_TOPUP: 'after_topup',
  },
  DAILY_QUEST_BY_HEART: 'daily_quest_by_heart',
  MESSAGE_BARS: 'message_bars',
  MESSAGE_BARS_TAB: {
    MESSAGE_BAR_PRIZE: 'message_bar_prize',
    MESSAGE_BAR_LEADER_BOARD: 'message_bar_leader_board',
  },
  EVENT_PROMOTION: 'event_promotion',
  EVENT_PROMOTION_TAB: {
    EN: 'event_promotion_en',
    MY: 'event_promotion_my',
  },
};

const extractBannerSrc = (html) => {
  const match = html?.match(/<img[^>]*\bsrc=["']([^"']+)["']/i);
  return match ? match[1] : '';
};

const appendStyle = (value) => {
  return `<style>p,h1,h2,h3,h4,h5,h6,ul,ol,li {margin-top: 0 !important; margin-bottom: 0 !important; padding-bottom: 0 !important; padding-top: 0 !important;} h1 {font-size: 1.75rem;} h2 {font-size: 1.5rem;} h3 {font-size: 1.25rem;} h4 {font-size: 1.125rem;} p,ul,ol,li,h5,h6 {font-size: 1rem;}</style><div>${value}</div>`;
};

function Configuration() {
  // forms
  const [generalForm] = Form.useForm();
  const [termsForm] = Form.useForm();
  const [prizeForm] = Form.useForm();
  const [smsForm] = Form.useForm();
  const [messageBarForm] = Form.useForm();
  const [dailyQuestByHeartForm] = Form.useForm();
  const [eventPromotionForm] = Form.useForm();

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () => getConfig(),
    ['config']
  );
  const configGeneralMutation = useMutation({
    mutationFn: updateConfigGeneral,
  });
  const configInfoMutation = useMutation({
    mutationFn: updateConfigInfo,
  });

  const configSmsMutation = useMutation({
    mutationFn: updateConfigSms,
  });

  const configDailyQuestByHeartMutation = useMutation({
    mutationFn: updateConfigDailyQuestByHeart,
  });

  const configEventPromotionMutation = useMutation({
    mutationFn: updateConfigEventPromotion,
  });

  const { hasUnsaved, setHasUnsaved } = useUnsaved();
  //states
  const [hasGeneralChanges, setHasGeneralChanges] = useState(false);
  const [hasTermsChanges, setHasTermsChanges] = useState(false);
  const [hasPrizeChanges, setHasPrizeChanges] = useState(false);
  const [hasSmsChanges, setHasSmsChanges] = useState(false);
  const [hasMessageBarChanges, setHasMessageBarChanges] = useState(false);
  const [hasDailyQuestByHeartChanges, setHasDailyQuestByHeartChanges] = useState(false);
  const [hasEventPromotionChanges, setHasEventPromotionChanges] = useState(false);

  const isInitializing = useRef(true);
  const initialValuesSet = useRef(false);

  const queryClient = useQueryClient();

  // global unsaved states
  useEffect(() => {
    const hasAnyChanges =
      hasGeneralChanges ||
      hasTermsChanges ||
      hasPrizeChanges ||
      hasSmsChanges ||
      hasDailyQuestByHeartChanges ||
      hasEventPromotionChanges;
    if (hasUnsaved !== hasAnyChanges) {
      setHasUnsaved(hasAnyChanges);
    }
  }, [
    hasGeneralChanges,
    hasTermsChanges,
    hasPrizeChanges,
    hasSmsChanges,
    hasUnsaved,
    setHasUnsaved,
    hasDailyQuestByHeartChanges,
    hasEventPromotionChanges,
  ]);
  
  const configMessageBarMutation = useMutation({
    mutationFn: updateConfigMessageBar,
  });

  const initialValues = useMemo(() => {
    if (!data) return {};

    let imageListEn = [];
    let imageListMy = [];
    let imageListDailyQuestByHeartEn = [];
    let imageListDailyQuestByHeartMy = [];
    let imageListEventPromotionIcon = [];
    let imageListEventPromotionTitleEn = [];
    let imageListEventPromotionTitleMy = [];
    let imageListEventPromotionBannerEn = [];
    let imageListEventPromotionBannerMy = [];
    const iconPrize = data?.find((item) => item.config_key === 'ICON_PRIZE_HOME');
    const scoreExchangeConfig = data?.find((item) => item.config_key === 'POINT_REDEEM_PIECE');
    const monthlyExchangeLimitConfig = data?.find(
      (item) => item.config_key === 'MAX_TURN_GIVE_RECEIVE_PIECE_MONTH'
    );
    const htmlTermConditionConfig = data?.find((item) => item.config_key === 'HTML_TERM_CONDITION');
    const prizeInfomationConfig = data?.find((item) => item.config_key === 'PRIZE_INFORMATION');
    const smsTopupConfig = data?.find((item) => item.config_key === 'MESSAGE_TAKE_PRIZE_TOPUP');
    const smsPhysicalConfig = data?.find(
      (item) => item.config_key === 'MESSAGE_TAKE_PRIZE_PHYSICAL'
    );
    const smsAfterTopup = data?.find((item) => item.config_key === 'MESSAGE_AFTER_TOPUP');
    const dailyQuestByHeartConfig = data?.find(
      (item) => item.config_key === 'DAILY_QUEST_BY_HEARTS'
    );
    const messageBarConfigPrize = data?.find((item) => item.config_key == 'MESSAGE_BAR_PRIZE');
    const messageBarConfigLeaderBoard = data?.find((item) => item.config_key == 'MESSAGE_BAR_LEADER_BOARD');
    const eventPromotionConfig = data?.find((item) => item.config_key === 'EVENT_PROMOTION');

    if (iconPrize?.config_value) {
      imageListEn = [
        {
          uid: '-1',
          name: `Prize Icon English`,
          status: 'done',
          url: `${process.env.REACT_APP_API_BASEURL}${iconPrize.config_value}`,
        },
      ];
    }

    if (iconPrize?.config_value_my) {
      imageListMy = [
        {
          uid: '-1',
          name: `Prize Icon Myanmar`,
          status: 'done',
          url: `${process.env.REACT_APP_API_BASEURL}${iconPrize.config_value_my}`,
        },
      ];
    }

    const parsedDailyQuestByHeart = safeJSONParse(dailyQuestByHeartConfig?.config_value);
    const parsedDailyQuestByHeartMy = safeJSONParse(dailyQuestByHeartConfig?.config_value_my);

    if (parsedDailyQuestByHeart?.image) {
      imageListDailyQuestByHeartEn = [
        {
          uid: '-1',
          name: `Daily Quest By Heart Icon English`,
          status: 'done',
          url: `${process.env.REACT_APP_API_BASEURL}${parsedDailyQuestByHeart.image}`,
        },
      ];
    }

    if (parsedDailyQuestByHeartMy?.image) {
      imageListDailyQuestByHeartMy = [
        {
          uid: '-1',
          name: `Daily Quest By Heart Icon Myanmar`,
          status: 'done',
          url: `${process.env.REACT_APP_API_BASEURL}${parsedDailyQuestByHeartMy.image}`,
        },
      ];
    }

    const parsedEventPromotion = safeJSONParse(eventPromotionConfig?.config_value);
    const parsedEventPromotionMy = safeJSONParse(eventPromotionConfig?.config_value_my);

    if (parsedEventPromotion?.icon) {
      imageListEventPromotionIcon = [
        {
          uid: '-1',
          name: `Event Promotion Icon`,
          status: 'done',
          url: parsedEventPromotion.icon,
        },
      ];
    }

    if (parsedEventPromotion?.title) {
      imageListEventPromotionTitleEn = [
        {
          uid: '-1',
          name: `Event Promotion Title English`,
          status: 'done',
          url: parsedEventPromotion.title,
        },
      ];
    }

    if (parsedEventPromotionMy?.title) {
      imageListEventPromotionTitleMy = [
        {
          uid: '-1',
          name: `Event Promotion Title Myanmar`,
          status: 'done',
          url: parsedEventPromotionMy.title,
        },
      ];
    }

    const eventPromotionBannerEnSrc = extractBannerSrc(parsedEventPromotion?.content);
    const eventPromotionBannerMySrc = extractBannerSrc(parsedEventPromotionMy?.content);

    if (eventPromotionBannerEnSrc) {
      imageListEventPromotionBannerEn = [
        {
          uid: '-1',
          name: `Event Promotion Banner English`,
          status: 'done',
          url: eventPromotionBannerEnSrc,
        },
      ];
    }

    if (eventPromotionBannerMySrc) {
      imageListEventPromotionBannerMy = [
        {
          uid: '-1',
          name: `Event Promotion Banner Myanmar`,
          status: 'done',
          url: eventPromotionBannerMySrc,
        },
      ];
    }

    return {
      general: {
        image_en: imageListEn,
        image_my: imageListMy,
        icon_id: iconPrize?.id,
        icon_key: iconPrize?.config_key,
        score_exchange_id: scoreExchangeConfig?.id,
        score_exchange_key: scoreExchangeConfig?.config_key,
        monthly_exchange_limit_id: monthlyExchangeLimitConfig?.id,
        monthly_exchange_limit_key: monthlyExchangeLimitConfig?.config_key,
        score_exchange: scoreExchangeConfig?.config_value,
        monthly_exchange_limit: monthlyExchangeLimitConfig?.config_value,
      },
      terms: {
        term_conditions_key: htmlTermConditionConfig?.config_key,
        term_conditions_id: htmlTermConditionConfig?.id,
        term_conditions_en: htmlTermConditionConfig?.config_value || '',
        term_conditions_my: htmlTermConditionConfig?.config_value_my || '',
      },
      prize: {
        prize_info_id: prizeInfomationConfig?.id,
        term_conditions_key: prizeInfomationConfig?.config_key,
        prize_info_en: prizeInfomationConfig?.config_value || '',
        prize_info_my: prizeInfomationConfig?.config_value_my || '',
      },
      sms_topup: {
        topup_prize_id: smsTopupConfig?.id,
        topup_prize_key: smsTopupConfig?.config_key,
        sms_topup_en: smsTopupConfig?.config_value || '',
        sms_topup_my: smsTopupConfig?.config_value_my || '',
      },
      sms_physical: {
        physical_prize_id: smsPhysicalConfig?.id,
        physical_prize_key: smsPhysicalConfig?.config_key,
        sms_physical_en: smsPhysicalConfig?.config_value || '',
        sms_physical_my: smsPhysicalConfig?.config_value_my || '',
      },
      sms_after_topup: {
        message_after_topup_id: smsAfterTopup?.id,
        message_after_topup_key: smsAfterTopup?.config_key,
        message_after_topup_en: smsAfterTopup?.config_value || '',
        message_after_topup_my: smsAfterTopup?.config_value_my || '',
      },
      daily_quest_by_heart: {
        daily_quest_by_heart_id: dailyQuestByHeartConfig?.id,
        daily_quest_by_heart_key: dailyQuestByHeartConfig?.config_key,
        daily_quest_by_heart_image_en: imageListDailyQuestByHeartEn,
        daily_quest_by_heart_image_my: imageListDailyQuestByHeartMy,
        daily_quest_by_heart_content_en: parsedDailyQuestByHeart?.content || '',
        daily_quest_by_heart_content_my: parsedDailyQuestByHeartMy?.content || '',
      },
      message_bar_config: {
        message_bar_prize_config_en: messageBarConfigPrize?.config_value || '',
        message_bar_prize_config_my: messageBarConfigPrize?.config_value_my || '',
        message_bar_leader_board_config_en: messageBarConfigLeaderBoard?.config_value || '',
        message_bar_leader_board_config_my: messageBarConfigLeaderBoard?.config_value_my || '',
      },
      event_promotion: {
        event_promotion_type: parsedEventPromotion?.type || '',
        event_promotion_is_show: !!parsedEventPromotion?.isShow,
        event_promotion_link_web: parsedEventPromotion?.link_web || '',
        event_promotion_link_webview: parsedEventPromotion?.link_webview || '',
        event_promotion_icon: imageListEventPromotionIcon,
        event_promotion_title_en: imageListEventPromotionTitleEn,
        event_promotion_title_my: imageListEventPromotionTitleMy,
        event_promotion_banner_en: imageListEventPromotionBannerEn,
        event_promotion_banner_my: imageListEventPromotionBannerMy,
        event_promotion_content_en: parsedEventPromotion?.content || '',
        event_promotion_content_my: parsedEventPromotionMy?.content || '',
      },
    };
  }, [data]);

  // media queries
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  // init values
  useEffect(() => {
    if (data && Object.keys(initialValues).length > 0 && !initialValuesSet.current) {
      generalForm.setFieldsValue(initialValues.general);
      termsForm.setFieldsValue(initialValues.terms);
      prizeForm.setFieldsValue(initialValues.prize);
      smsForm.setFieldsValue({
        sms_topup_config_en: initialValues.sms_topup.sms_topup_en,
        sms_topup_config_my: initialValues.sms_topup.sms_topup_my,
        sms_physical_config_en: initialValues.sms_physical.sms_physical_en,
        sms_physical_config_my: initialValues.sms_physical.sms_physical_my,
        sms_after_topup_config_en: initialValues.sms_after_topup.message_after_topup_en,
        sms_after_topup_config_my: initialValues.sms_after_topup.message_after_topup_my,
      });
      messageBarForm.setFieldsValue({
        message_bar_prize_config_en:
          initialValues?.message_bar_config?.message_bar_prize_config_en,
        message_bar_prize_config_my: initialValues?.message_bar_config?.message_bar_prize_config_my,
        message_bar_leader_board_config_en: initialValues?.message_bar_config?.message_bar_leader_board_config_en,
        message_bar_leader_board_config_my: initialValues?.message_bar_config?.message_bar_leader_board_config_my,
      });
      dailyQuestByHeartForm.setFieldsValue(initialValues.daily_quest_by_heart);
      eventPromotionForm.setFieldsValue(initialValues.event_promotion);
      initialValuesSet.current = true;

      setTimeout(() => {
        isInitializing.current = false;
        setHasGeneralChanges(false);
        setHasTermsChanges(false);
        setHasPrizeChanges(false);
        setHasSmsChanges(false);
        setHasMessageBarChanges(false);
        setHasDailyQuestByHeartChanges(false);
        setHasEventPromotionChanges(false);
        setHasUnsaved(false);
      }, 500);
    }
  }, [
    data,
    initialValues,
    generalForm,
    termsForm,
    messageBarForm,
    prizeForm,
    smsForm,
    dailyQuestByHeartForm,
    eventPromotionForm,
    setHasUnsaved,
  ]);

  const onGeneralSubmit = useFormSubmit(
    configGeneralMutation,
    async (values) => {
      const imageFileEn = values.image_en?.[0]?.originFileObj || values.image_en?.[0];
      const imageFileMy = values.image_my?.[0]?.originFileObj || values.image_my?.[0];
      const payload = {
        score_ex: {
          key: initialValues.general.score_exchange_key,
          data: values.score_exchange,
        },
        ex_limit: {
          key: initialValues.general.monthly_exchange_limit_key,
          data: values.monthly_exchange_limit,
        },
        icon_key: initialValues.general.icon_key,
      };

      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== null && payload[key] !== undefined) {
          if (typeof payload[key] === 'object') {
            formData.append(key, JSON.stringify(payload[key]));
          } else {
            formData.append(key, payload[key]);
          }
        }
      });

      if (imageFileEn) {
        formData.append('icon_prize_image_en', imageFileEn);
      }

      if (imageFileMy) {
        formData.append('icon_prize_image_my', imageFileMy);
      }

      return formData;
    },
    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
          },
          afterClose: () => {
            setHasGeneralChanges(false);
          },
        });
      },
    }
  );

  const onTermsSubmit = useFormSubmit(
    configInfoMutation,
    (values) => {
      const payload = {
        // id: initialValues.terms.term_conditions_id,
        key: initialValues.terms.term_conditions_key,
        value: {
          en: appendStyle(values.term_conditions_en),
          my: appendStyle(values.term_conditions_my),
        },
      };

      return payload;
    },

    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
          },
          afterClose: () => {
            setHasTermsChanges(false);
          },
        });
      },
    }
  );

  const onPrizeSubmit = useFormSubmit(
    configInfoMutation,
    (values) => {
      const payload = {
        key: initialValues.prize.term_conditions_key,
        value: {
          en: appendStyle(values.prize_info_en),
          my: appendStyle(values.prize_info_my),
        },
      };
      return payload;
    },
    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
          },
          afterClose: () => {
            setHasPrizeChanges(false);
          },
        });
      },
    }
  );

  const onSmsSubmit = useFormSubmit(
    configSmsMutation,
    (values) => {
      const payload = {
        top_up_prize: {
          key: initialValues.sms_topup.topup_prize_key,
          value_en: values.sms_topup_config_en,
          value_my: values.sms_topup_config_my,
        },
        physical_prize: {
          key: initialValues.sms_physical.physical_prize_key,
          value_en: values.sms_physical_config_en,
          value_my: values.sms_physical_config_my,
        },
        sms_after_topup: {
          key: initialValues.sms_after_topup.message_after_topup_key,
          value_en: values.sms_after_topup_config_en,
          value_my: values.sms_after_topup_config_my,
        },
      };
      return payload;
    },
    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
          },
          afterClose: () => {
            setHasSmsChanges(false);
          },
        });
      },
    }
  );

  const onMessageBarSubmit = useFormSubmit(
    configMessageBarMutation,
    (values) => {
      const payload = {
        message_bar_prize: {
          key: 'MESSAGE_BAR_PRIZE',
          value_en: values.message_bar_prize_config_en,
          value_my: values.message_bar_prize_config_my,
        },
        message_bar_leader_board: {
          key: 'MESSAGE_BAR_LEADER_BOARD',
          value_en: values.message_bar_leader_board_config_en,
          value_my: values.message_bar_leader_board_config_my,
        },
      };
      return payload;
    },
    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
          },
          afterClose: () => {
            setHasMessageBarChanges(false);
          },
        });
      },
    }

  );

  const onDailyQuestByHeartSubmit = useFormSubmit(
    configDailyQuestByHeartMutation,
    async (values) => {
      const imageFileEn =
        values.daily_quest_by_heart_image_en?.[0]?.originFileObj ||
        values.daily_quest_by_heart_image_en?.[0];
      const imageFileMy =
        values.daily_quest_by_heart_image_my?.[0]?.originFileObj ||
        values.daily_quest_by_heart_image_my?.[0];
      const payload = {
        key: initialValues.daily_quest_by_heart.daily_quest_by_heart_key,
        content_en: values.daily_quest_by_heart_content_en,
        content_my: values.daily_quest_by_heart_content_my,
      };

      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== null && payload[key] !== undefined) {
          if (typeof payload[key] === 'object') {
            formData.append(key, JSON.stringify(payload[key]));
          } else {
            formData.append(key, payload[key]);
          }
        }
      });

      if (imageFileEn) {
        formData.append('image_en', imageFileEn);
      }
      if (imageFileMy) {
        formData.append('image_my', imageFileMy);
      }

      return formData;
    },
    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
          },
          afterClose: () => {
            setHasDailyQuestByHeartChanges(false);
          },
        });
      },
    }
  );

  const onEventPromotionSubmit = useFormSubmit(
    configEventPromotionMutation,
    async (values) => {
      const iconFile =
        values.event_promotion_icon?.[0]?.originFileObj || values.event_promotion_icon?.[0];
      const titleFileEn =
        values.event_promotion_title_en?.[0]?.originFileObj ||
        values.event_promotion_title_en?.[0];
      const titleFileMy =
        values.event_promotion_title_my?.[0]?.originFileObj ||
        values.event_promotion_title_my?.[0];
      const bannerFileEn =
        values.event_promotion_banner_en?.[0]?.originFileObj ||
        values.event_promotion_banner_en?.[0];
      const bannerFileMy =
        values.event_promotion_banner_my?.[0]?.originFileObj ||
        values.event_promotion_banner_my?.[0];

      const payload = {
        type: values.event_promotion_type,
        isShow: values.event_promotion_is_show,
        link_web: values.event_promotion_link_web,
        link_webview: values.event_promotion_link_webview,
      };

      // content_en/content_my chỉ gửi khi người dùng chủ động sửa tay HTML nâng cao,
      // để không ghi đè việc BE tự thay src ảnh banner trong content hiện tại.
      if (eventPromotionForm.isFieldsTouched(['event_promotion_content_en'])) {
        payload.content_en = values.event_promotion_content_en;
      }
      if (eventPromotionForm.isFieldsTouched(['event_promotion_content_my'])) {
        payload.content_my = values.event_promotion_content_my;
      }

      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== null && payload[key] !== undefined) {
          formData.append(key, payload[key]);
        }
      });

      if (iconFile) {
        formData.append('icon', iconFile);
      }
      if (titleFileEn) {
        formData.append('title_en', titleFileEn);
      }
      if (titleFileMy) {
        formData.append('title_my', titleFileMy);
      }
      if (bannerFileEn) {
        formData.append('banner_en', bannerFileEn);
      }
      if (bannerFileMy) {
        formData.append('banner_my', bannerFileMy);
      }

      return formData;
    },
    {
      onSuccess: () => {
        SuccessResponseModal({
          onOk: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
          },
          afterClose: () => {
            setHasEventPromotionChanges(false);
          },
        });
      },
    }
  );

  // form change handlers
  const handleGeneralChange = () => {
    if (!isInitializing.current && initialValuesSet.current) {
      const hasChanges = generalForm.isFieldsTouched();
      setHasGeneralChanges(hasChanges);
    }
  };

  const handleTermsChange = () => {
    if (!isInitializing.current && initialValuesSet.current) {
      const hasChanges = termsForm.isFieldsTouched();
      setHasTermsChanges(hasChanges);
    }
  };

  const handlePrizeChange = () => {
    if (!isInitializing.current && initialValuesSet.current) {
      const hasChanges = prizeForm.isFieldsTouched();
      setHasPrizeChanges(hasChanges);
    }
  };

  const handleSmsChange = () => {
    if (!isInitializing.current && initialValuesSet.current) {
      const hasChanges = smsForm.isFieldsTouched();
      setHasSmsChanges(hasChanges);
    }
  };

  const handleMessageBarChange = () => {
    if (!isInitializing.current && initialValuesSet.current) {
      const hasChanges = messageBarForm.isFieldsTouched();
      setHasMessageBarChanges(hasChanges);
    }
  };

  const handleDailyQuestByHeartChange = () => {
    if (!isInitializing.current && initialValuesSet.current) {
      const hasChanges = dailyQuestByHeartForm.isFieldsTouched();
      setHasDailyQuestByHeartChanges(hasChanges);
    }
  };

  const handleEventPromotionChange = () => {
    if (!isInitializing.current && initialValuesSet.current) {
      const hasChanges = eventPromotionForm.isFieldsTouched();
      setHasEventPromotionChanges(hasChanges);
    }
  };

  const tabItems = [
    {
      key: TAB_KEY.GENERAL,
      label: 'General',
      children: (
        <AppForm
          isCard={false}
          form={generalForm}
          noCancel={true}
          onSubmit={onGeneralSubmit}
          initialValues={initialValues.general}
          buttonProps={{
            loading: configGeneralMutation.isPending,
            saveDisabled: !hasGeneralChanges,
          }}
          disabled={configGeneralMutation.isPending || isLoading || isRefetching}
          onValuesChange={handleGeneralChange}
        >
          <Form.Item
            label="Prize Icon English"
            name="image_en"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
            rules={[{ message: 'Please upload prize icon English', required: true }]}
            initialValue={[]}
          >
            <FormUpload beforeUpload={() => false} />
          </Form.Item>
          <Form.Item
            label="Prize Icon Myanmar"
            name="image_my"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
            rules={[{ message: 'Please upload prize icon Myanmar', required: true }]}
            initialValue={[]}
          >
            <FormUpload beforeUpload={() => false} />
          </Form.Item>
          <Form.Item
            label="Score Exchange"
            name="score_exchange"
            rules={[{ message: 'Please enter score exchange', required: true }]}
          >
            <InputNumber
              placeholder="Enter score exchange"
              className="w-full"
              min={0}
              formatter={(value) => formatNumber(value)}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
          <Form.Item
            label="Monthly Exchange Limit"
            name="monthly_exchange_limit"
            rules={[{ message: 'Please enter monthly exchange limit', required: true }]}
          >
            <InputNumber
              placeholder="Enter monthly exchange limit"
              className="w-full"
              min={0}
              formatter={(value) => formatNumber(value)}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </AppForm>
      ),
    },
    {
      key: TAB_KEY.TERM_CONDITIONS,
      label: 'Terms & Conditions',
      children: (
        <AppForm
          isCard={false}
          form={termsForm}
          noCancel={true}
          onSubmit={onTermsSubmit}
          initialValues={initialValues.terms}
          buttonProps={{
            loading: configInfoMutation.isPending,
            saveDisabled: !hasTermsChanges,
          }}
          disabled={configInfoMutation.isPending || isLoading || isRefetching}
          onValuesChange={handleTermsChange}
        >
          <Form.Item
            label="English"
            name="term_conditions_en"
            rules={[{ message: 'Please enter terms and conditions', required: true }]}
          >
            <AppEditor
              placeholder="Enter terms and conditions..."
              style={{
                height: '500px',
              }}
              disabled={configInfoMutation.isPending || isLoading || isRefetching}
            />
          </Form.Item>

          <Form.Item
            label="Myanmar"
            name="term_conditions_my"
            rules={[{ message: 'Please enter terms and conditions', required: true }]}
          >
            <AppEditor
              placeholder="Enter terms and conditions..."
              style={{
                height: '500px',
              }}
              disabled={configInfoMutation.isPending || isLoading || isRefetching}
            />
          </Form.Item>
        </AppForm>
      ),
    },
    {
      key: TAB_KEY.PRIZE_INFO,
      label: 'Prize Information',
      children: (
        <AppForm
          isCard={false}
          form={prizeForm}
          noCancel={true}
          onSubmit={onPrizeSubmit}
          initialValues={initialValues.prize}
          buttonProps={{
            loading: configInfoMutation.isPending,
            saveDisabled: !hasPrizeChanges,
          }}
          disabled={configInfoMutation.isPending || isLoading || isRefetching}
          onValuesChange={handlePrizeChange}
        >
          <Form.Item
            label="English"
            name="prize_info_en"
            rules={[{ message: 'Please enter prize information', required: true }]}
          >
            <AppEditor
              placeholder="Enter prize information..."
              style={{
                height: '500px',
              }}
              disabled={configInfoMutation.isPending || isLoading || isRefetching}
            />
          </Form.Item>

          <Form.Item
            label="Myanmar"
            name="prize_info_my"
            rules={[{ message: 'Please enter prize information', required: true }]}
          >
            <AppEditor
              placeholder="Enter prize information..."
              style={{
                height: '500px',
              }}
              disabled={configInfoMutation.isPending || isLoading || isRefetching}
            />
          </Form.Item>
        </AppForm>
      ),
    },
    {
      key: TAB_KEY.SMS.ROOT,
      label: 'SMS',
      children: (
        <AppForm
          isCard={false}
          form={smsForm}
          noCancel={true}
          onSubmit={onSmsSubmit}
          initialValues={initialValues.sms}
          buttonProps={{
            loading: configSmsMutation.isPending,
            saveDisabled: !hasSmsChanges,
          }}
          disabled={configSmsMutation.isPending || isLoading || isRefetching}
          onValuesChange={handleSmsChange}
        >
          <Tabs tabPosition={isMobile ? 'top' : 'left'} defaultActiveKey={TAB_KEY.SMS.TOPUP_PRIZE}>
            <Tabs.TabPane tab="Topup Prize" key={TAB_KEY.SMS.TOPUP_PRIZE}>
              <Form.Item
                label="English"
                name="sms_topup_config_en"
                // rules={[{ message: 'Please enter prize information', required: true }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>

              <Form.Item
                label="Myanmar"
                name="sms_topup_config_my"
                // rules={[{ message: 'Please enter prize information', required: true }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Physical Prize" key={TAB_KEY.SMS.PHYSICAL_PRIZE}>
              <Form.Item
                label="English"
                name="sms_physical_config_en"
                // rules={[{ message: 'Please enter message', required: true }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>

              <Form.Item
                label="Myanmar"
                name="sms_physical_config_my"
                // rules={[{ message: 'Please enter message', required: true }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="After Topup" key={TAB_KEY.SMS.AFTER_TOPUP}>
              <Form.Item
                label="English"
                name="sms_after_topup_config_en"
                // rules={[{ message: 'Please enter message', required: true }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>

              <Form.Item
                label="Myanmar"
                name="sms_after_topup_config_my"
                // rules={[{ message: 'Please enter message', required: true }]}
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>
        </AppForm>
      ),
    },
    {
      key: TAB_KEY.DAILY_QUEST_BY_HEART,
      label: 'Daily Quest By Heart',
      children: (
        <AppForm
          isCard={false}
          form={dailyQuestByHeartForm}
          noCancel={true}
          onSubmit={onDailyQuestByHeartSubmit}
          initialValues={initialValues.daily_quest_by_heart}
          buttonProps={{
            loading: configDailyQuestByHeartMutation.isPending,
            saveDisabled: !hasDailyQuestByHeartChanges,
          }}
          disabled={configDailyQuestByHeartMutation.isPending || isLoading || isRefetching}
          onValuesChange={handleDailyQuestByHeartChange}
        >
          <Form.Item
            label="English Image"
            name="daily_quest_by_heart_image_en"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
            rules={[{ message: 'Please upload prize icon English', required: true }]}
            initialValue={[]}
          >
            <FormUpload beforeUpload={() => false} />
          </Form.Item>
          <Form.Item
            label="English Content"
            name="daily_quest_by_heart_content_en"
            rules={[
              { message: 'Please enter daily quest by heart content English', required: true },
            ]}
          >
            <Input placeholder="Enter daily quest by heart content English" className="w-full" />
          </Form.Item>
          <Form.Item
            label="Myanmar Image"
            name="daily_quest_by_heart_image_my"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
            rules={[{ message: 'Please upload prize icon Myanmar', required: true }]}
            initialValue={[]}
          >
            <FormUpload beforeUpload={() => false} />
          </Form.Item>
          <Form.Item
            label="Myanmar Content"
            name="daily_quest_by_heart_content_my"
            rules={[
              { message: 'Please enter daily quest by heart content Myanmar', required: true },
            ]}
          >
            <Input
              placeholder="Enter daily quest by heart content Myanmar"
              className="w-full"
            />
          </Form.Item>
        </AppForm>
      ),
    },
    {
      key: TAB_KEY.MESSAGE_BARS,
      label: 'Message Bars',
      children: (
        <AppForm
          isCard={false}
          form={messageBarForm}
          noCancel={true}
          onSubmit={onMessageBarSubmit}
          buttonProps={{
            loading: configMessageBarMutation.isPending,
            saveDisabled: !hasMessageBarChanges,
          }}
          disabled={configMessageBarMutation.isPending || isLoading || isRefetching}
          onValuesChange={handleMessageBarChange}
        >
          <Tabs
            tabPosition={isMobile ? 'top' : 'left'}
            defaultActiveKey={TAB_KEY.MESSAGE_BARS_TAB.MESSAGE_BAR_PRIZE}
          >
            <Tabs.TabPane tab="Prize" key={TAB_KEY.MESSAGE_BARS_TAB.MESSAGE_BAR_PRIZE}>
              <Form.Item
                label="English"
                name="message_bar_prize_config_en"
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>

              <Form.Item
                label="Myanmar"
                name="message_bar_prize_config_my"
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Leader Board" key={TAB_KEY.MESSAGE_BAR_LEADER_BOARD}>
              <Form.Item
                label="English"
                name="message_bar_leader_board_config_en"
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>

              <Form.Item
                label="Myanmar"
                name="message_bar_leader_board_config_my"
              >
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 10 }}
                  placeholder="Please enter message..."
                ></Input.TextArea>
              </Form.Item>
            </Tabs.TabPane>
          </Tabs>
        </AppForm>
      ),
    },
    {
      key: TAB_KEY.EVENT_PROMOTION,
      label: 'Event Promotion',
      children: (
        <AppForm
          isCard={false}
          form={eventPromotionForm}
          noCancel={true}
          onSubmit={onEventPromotionSubmit}
          initialValues={initialValues.event_promotion}
          buttonProps={{
            loading: configEventPromotionMutation.isPending,
            saveDisabled: !hasEventPromotionChanges,
          }}
          disabled={configEventPromotionMutation.isPending || isLoading || isRefetching}
          onValuesChange={handleEventPromotionChange}
        >
          <Form.Item
            label="Show Banner"
            name="event_promotion_is_show"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item label="Type" name="event_promotion_type">
            <Input placeholder="Enter event type, e.g. event" className="w-full" />
          </Form.Item>
          <Form.Item label="Link Web" name="event_promotion_link_web">
            <Input placeholder="Enter link opened via web" className="w-full" />
          </Form.Item>
          <Form.Item label="Link Webview" name="event_promotion_link_webview">
            <Input placeholder="Enter link opened via app webview" className="w-full" />
          </Form.Item>
          <Form.Item
            label="Icon (shared EN/MY)"
            name="event_promotion_icon"
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              if (Array.isArray(e)) {
                return e;
              }
              return e?.fileList;
            }}
            initialValue={[]}
          >
            <FormUpload beforeUpload={() => false} />
          </Form.Item>
          <Tabs
            tabPosition={isMobile ? 'top' : 'left'}
            defaultActiveKey={TAB_KEY.EVENT_PROMOTION_TAB.EN}
          >
            <Tabs.TabPane tab="English" key={TAB_KEY.EVENT_PROMOTION_TAB.EN}>
              <Form.Item
                label="Title Image"
                name="event_promotion_title_en"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
                initialValue={[]}
              >
                <FormUpload beforeUpload={() => false} />
              </Form.Item>
              <Form.Item
                label="Banner Image"
                name="event_promotion_banner_en"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
                initialValue={[]}
              >
                <FormUpload beforeUpload={() => false} />
              </Form.Item>
              <Collapse
                className="mt-4"
                items={[
                  {
                    key: 'advanced_content_en',
                    label: 'Advanced: Manually edit full HTML content (rarely needed)',
                    children: (
                      <Form.Item
                        label="Content HTML"
                        name="event_promotion_content_en"
                      >
                        <Input.TextArea
                          autoSize={{ minRows: 6, maxRows: 20 }}
                          placeholder="Enter event content HTML (English)..."
                        />
                      </Form.Item>
                    ),
                  },
                ]}
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab="Myanmar" key={TAB_KEY.EVENT_PROMOTION_TAB.MY}>
              <Form.Item
                label="Title Image"
                name="event_promotion_title_my"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
                initialValue={[]}
              >
                <FormUpload beforeUpload={() => false} />
              </Form.Item>
              <Form.Item
                label="Banner Image"
                name="event_promotion_banner_my"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e?.fileList;
                }}
                initialValue={[]}
              >
                <FormUpload beforeUpload={() => false} />
              </Form.Item>
              <Collapse
                className="mt-4"
                items={[
                  {
                    key: 'advanced_content_my',
                    label: 'Advanced: Manually edit full HTML content (rarely needed)',
                    children: (
                      <Form.Item
                        label="Content HTML"
                        name="event_promotion_content_my"
                      >
                        <Input.TextArea
                          autoSize={{ minRows: 6, maxRows: 20 }}
                          placeholder="Enter event content HTML (Myanmar)..."
                        />
                      </Form.Item>
                    ),
                  },
                ]}
              />
            </Tabs.TabPane>
          </Tabs>
        </AppForm>
      ),
    },
  ];

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle
        title="Configuration"
        extra={
          <Tooltip title="Refresh">
            <Button
              type="text"
              icon={<SyncOutlined spin={isRefetching} />}
              onClick={refetch}
              disabled={isLoading || isRefetching}
            />
          </Tooltip>
        }
      />

      <Card>
        <Tabs items={tabItems} defaultActiveKey={TAB_KEY.GENERAL} />
      </Card>
    </Space>
  );
}

export default Configuration;
